// src/pages/AdminDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { collection, collectionGroup, onSnapshot, query, orderBy, getDocs, doc, onSnapshot as onDoc, getDoc, where } from 'firebase/firestore';
import webSocketService from '../services/websocketService';
import CommandHud from '../components/dashboard/CommandHud.jsx';
import { calculatePolicyCompliance } from '../../../shared/utils/policyComplianceCalculator';
import { calculateUsersWithCompliance, calculatePolicyComplianceStats } from '../../../shared/utils/policyComplianceStatsCalculator';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig.jsx';
const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [remits, setRemits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [consults, setConsults] = useState([]);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [range, setRange] = useState('30'); // '7' | '30' | '90' | 'YTD'
  const [consultCounts, setConsultCounts] = useState({ total: 0, answered: 0 });
  const [wsConnected, setWsConnected] = useState(false);
  const [complianceStats, setComplianceStats] = useState({ 
    total: 0, 
    compliant: 0, 
    nonCompliant: 0, 
    averageCompliance: 0 
  });
  const [settings, setSettings] = useState({ requiredMonthlyAmount: 300 });
  const [userStats, setUserStats] = useState({
    employed: 0,
    total: 0,
    active: 0,
    inactive: 0,
    unemployed: 0,
    contractExpired: 0,
    contractActive: 0,
    noEmploymentData: 0
  });
  // Cloud Functions 호출
  const getNonCompliantStats = httpsCallable(functions, 'getNonCompliantStats');

  // Policy Compliance 통계 데이터 로드 (캐시 우선, Policy Compliance Management와 동일한 방식)
  const loadComplianceStatsFromCloud = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Policy Compliance 통계 데이터 로드 시작...');
      }
      
      // 1. 캐시에서 먼저 로드 시도 (Policy Compliance Management와 동일한 방식)
      // 🔥 CRITICAL FIX: 동적 import 제거 - 정적 import 사용 (TDZ 오류 해결)
      try {
        const { doc, getDoc, collection } = await import('firebase/firestore');
        // 정적 import된 db, appId 사용
        
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const statsDocRef = doc(adminCollectionRef, 'policyComplianceStats');
        const statsSnapshot = await getDoc(statsDocRef);
        
        if (statsSnapshot.exists()) {
          const statsFromCache = statsSnapshot.data();
          const cacheAge = statsFromCache.lastUpdated 
            ? new Date() - statsFromCache.lastUpdated.toDate()
            : Infinity;
          const cacheAgeHours = cacheAge / (1000 * 60 * 60);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`📊 캐시에서 Policy Compliance 통계 로드 (${cacheAgeHours.toFixed(1)}시간 전 업데이트)`);
          }
          
          // Policy Compliance 통계 업데이트 (캐시 데이터 사용)
          setComplianceStats({
            total: statsFromCache.totalUsers || 0,
            compliant: statsFromCache.compliantUsers || 0,
            nonCompliant: statsFromCache.nonCompliantUsers || 0,
            averageCompliance: statsFromCache.averageComplianceRate || 0 // Policy Compliance Management와 동일한 값
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ 캐시에서 Policy Compliance 통계 로드 완료');
          }
          
          return; // 캐시에서 성공적으로 로드했으면 종료
        }
      } catch (cacheError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ 캐시 로드 실패, Cloud Function으로 폴백:', cacheError.message);
        }
      }
      
      // 2. 캐시가 없거나 실패한 경우 Cloud Function 사용
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Cloud Functions에서 통계 데이터 로드 시작...');
      }
      const result = await getNonCompliantStats({});
      
      if (result.data.success) {
        const { totalUsers, nonCompliantUsers, averageComplianceRate, complianceAchieved } = result.data.data;
        
        // Policy Compliance 통계 업데이트
        setComplianceStats({
          total: totalUsers,
          compliant: totalUsers - nonCompliantUsers,
          nonCompliant: nonCompliantUsers,
          averageCompliance: averageComplianceRate // Policy Compliance Management와 동일한 값
        });
        
        // 사용자 통계 업데이트 (Employed Users 카드용)
        // Cloud Functions에서 가져온 데이터로 userStats 업데이트
        setUserStats(prev => ({
          ...prev,
          employed: totalUsers - nonCompliantUsers, // 고용중인 사용자 수 (전체 - 비준수)
          total: totalUsers, // 전체 사용자 수
          active: totalUsers, // 활성 사용자 수 (전체와 동일)
          inactive: 0, // 비활성 사용자 수 (Cloud Functions에서 제공하지 않음)
          unemployed: nonCompliantUsers, // 비준수 사용자 수
          contractExpired: 0, // Cloud Functions에서 제공하지 않음
          contractActive: totalUsers - nonCompliantUsers, // 고용중인 사용자 수
          noEmploymentData: 0 // Cloud Functions에서 제공하지 않음
        }));
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Cloud Functions 통계 로드 완료');
        }
      }
    } catch (error) {
      console.error('❌ Cloud Functions 통계 로드 오류:', error);
    }
  };

  // Process stages state for Remittance Process Monitoring (변경된 프로세스: WAITING_DEPOSIT, PENDING 제거)
  const [processStages, setProcessStages] = useState({
    stage1: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // PROCESSING: 헥토 해외송금 처리 중
    stage2: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // UAB_PROCESSING: 수취은행 송금 처리 중
    stage3: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // COMPLETED: 송금 완료
    stage4: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }  // FAILED: 실패
  });

  // 설정 로드
  const loadSettings = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 AdminDashboard 설정 로드 시작...');
      }
      const settingsRef = doc(db, 'artifacts', appId, 'settings', 'policy_compliance');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const loadedSettings = settingsDoc.data();
        setSettings(loadedSettings);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔧 AdminDashboard 설정 로드 완료');
        }
      }
    } catch (error) {
      console.error('설정 로드 오류:', error);
    }
  };

  // Calculate compliance statistics for all users (Policy Compliance Management System과 동일한 로직)
  const calculateComplianceStats = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 AdminDashboard Policy Compliance 계산 시작');
      }
      
      // Policy Compliance Management System과 동일한 방식으로 사용자 로드
      const usersRef = collection(db, 'artifacts', appId, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const userList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const firebaseUtils = { db, collection, getDocs, query, where };
      
      // 공통 함수를 사용하여 사용자별 Policy Compliance 계산 (설정값 포함)
      const usersWithCompliance = await calculateUsersWithCompliance(
        userList, 
        (userId, appId, firebaseUtils) => calculatePolicyCompliance(userId, appId, firebaseUtils, { requiredMonthlyAmount: settings.requiredMonthlyAmount }), 
        firebaseUtils, 
        appId
      );
      
      // 고용중인 사용자만 필터링해서 확인
      const employedUsers = usersWithCompliance.filter(u => u.status === 'employed');
      
      // 공통 함수를 사용하여 통계 계산
      const stats = calculatePolicyComplianceStats(usersWithCompliance);
      
      // Policy Compliance 통계 업데이트
      setComplianceStats({
        total: stats.total,
        compliant: stats.compliant,
        nonCompliant: stats.nonCompliant,
        averageCompliance: stats.averageCompliance
      });
      
      // 사용자 통계 업데이트 (Employed Users 카드용)
      setUserStats(prev => ({
        ...prev,
        employed: employedUsers.length, // 고용중인 사용자 수
        total: userList.length, // 전체 사용자 수
        active: userList.filter(u => u.active !== false).length, // 활성 사용자 수
        inactive: userList.filter(u => u.active === false).length, // 비활성 사용자 수
        unemployed: usersWithCompliance.filter(u => u.status === 'unemployed' || u.status === 'no_employment_data').length,
        contractExpired: usersWithCompliance.filter(u => {
          if (!u.contractEndDate) return false;
          const endDate = new Date(u.contractEndDate);
          const today = new Date();
          return endDate < today;
        }).length,
        contractActive: usersWithCompliance.filter(u => {
          if (!u.contractEndDate) return false;
          const endDate = new Date(u.contractEndDate);
          const today = new Date();
          return endDate >= today;
        }).length,
        noEmploymentData: usersWithCompliance.filter(u => u.status === 'no_employment_data').length
      }));
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 AdminDashboard 사용자 통계 업데이트:', {
          employed: employedUsers.length,
          total: userList.length,
          active: userList.filter(u => u.active !== false).length
        });
      }
    } catch (error) {
      console.error('Compliance 통계 계산 오류:', error);
    }
  };

  // Load users with profile data (exclude admin users) - same logic as UserManagementPage
  const loadUsersWithProfiles = async () => {
    try {
      const APP_KEY = appId || appId;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 AdminDashboard 사용자 로딩 시작, appId:', APP_KEY);
      }
      const usersData = [];
      const usersById = new Map(); // ID 기반 중복 체크
      const usersByEmail = new Map(); // Email 기반 중복 체크 (ID가 다를 수 있음)
      
      // 1. 새로운 경로: artifacts/{appId}/users (우선순위 1)
      try {
        const usersRef = collection(db, `artifacts/${APP_KEY}/users`);
        const q = query(usersRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 AdminDashboard 경로 1 사용자 문서 수:', snapshot.docs.length);
        }
        
        for (const userDoc of snapshot.docs) {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Skip admin users - same logic as UserManagementPage
          const isAdmin = userData.role === 'admin' || userData.admin === true;
          
          if (isAdmin) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🚫 AdminDashboard Admin 사용자 제외:', userId);
            }
            continue;
          }
          
          // 프로필 서브컬렉션에서 상세 정보 가져오기
          try {
            const profileRef = doc(db, `artifacts/${APP_KEY}/users/${userId}/data/profile`);
            const profileSnap = await getDoc(profileRef);
            
            const userObj = {
              id: userId,
              ...userData,
              fullName: userData.fullName || userData.name || userData.email?.split('@')[0],
              name: userData.fullName || userData.name || userData.email?.split('@')[0],
              source: 'artifacts/users'
            };
            
            if (profileSnap.exists()) {
              const profileData = profileSnap.data();
              userObj.fullName = profileData.fullName || profileData.name || userData.email?.split('@')[0];
              userObj.name = profileData.fullName || profileData.name || userData.email?.split('@')[0];
              Object.assign(userObj, profileData);
            }
            
            usersData.push(userObj);
            usersById.set(userId, userObj);
            if (userData.email) {
              usersByEmail.set(userData.email.toLowerCase(), userObj);
            }
          } catch (profileError) {
            console.warn(`AdminDashboard 프로필 로드 실패 (${userId}):`, profileError);
            const userObj = {
              id: userId,
              ...userData,
              fullName: userData.email?.split('@')[0],
              name: userData.email?.split('@')[0],
              source: 'artifacts/users'
            };
            usersData.push(userObj);
            usersById.set(userId, userObj);
            if (userData.email) {
              usersByEmail.set(userData.email.toLowerCase(), userObj);
            }
          }
        }
      } catch (error) {
        console.warn('AdminDashboard 경로 1 로드 실패:', error);
      }
      
      // 2. 이전 경로: users (직접) - 중복 체크 강화
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('📍 AdminDashboard 경로 2: users');
        }
        const oldUsersRef = collection(db, 'users');
        const oldSnapshot = await getDocs(oldUsersRef);
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 AdminDashboard 경로 2 사용자 문서 수:', oldSnapshot.docs.length);
        }
        
        for (const userDoc of oldSnapshot.docs) {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // ID 기반 중복 체크
          if (usersById.has(userId)) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⏭️ AdminDashboard 이미 추가된 사용자 (ID):', userId);
            }
            continue;
          }
          
          // Email 기반 중복 체크 (ID가 다를 수 있음)
          if (userData.email && usersByEmail.has(userData.email.toLowerCase())) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⏭️ AdminDashboard 이미 추가된 사용자 (Email):', userData.email, '(기존 ID:', usersByEmail.get(userData.email.toLowerCase()).id, ')');
            }
            continue;
          }
          
          // Skip admin users
          const isAdmin = userData.role === 'admin' || userData.admin === true;
          
          if (isAdmin) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🚫 AdminDashboard Admin 사용자 제외:', userId, userData.email, 'role:', userData.role, 'admin:', userData.admin);
            }
            continue;
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ AdminDashboard 일반 사용자 포함:', userId, userData.email, 'role:', userData.role, 'admin:', userData.admin);
          }
          
          const userObj = {
            id: userId,
            ...userData,
            fullName: userData.fullName || userData.name || userData.email?.split('@')[0],
            name: userData.fullName || userData.name || userData.email?.split('@')[0],
            source: 'users'
          };
          
          usersData.push(userObj);
          usersById.set(userId, userObj);
          if (userData.email) {
            usersByEmail.set(userData.email.toLowerCase(), userObj);
          }
        }
      } catch (error) {
        console.warn('AdminDashboard 경로 2 로드 실패:', error);
      }
      
      const sorted = usersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('👥 AdminDashboard Frontend 사용자 수:', sorted.length, '(Admin 사용자 제외)');
      console.log('📊 AdminDashboard 사용자 상세 정보:', sorted.map(u => ({
        id: u.id,
        email: u.email,
        role: u.role,
        admin: u.admin,
        createdAt: u.createdAt,
        source: u.source
      })));
      setUsers(sorted);
      setTotalUsersCount(sorted.length);
      // Load consultations when users change - sorted 배열을 직접 전달하여 users state 의존성 제거
      if (sorted && sorted.length > 0) {
        loadConsultationsDirectly(sorted);
      }
    } catch (error) {
      console.error('AdminDashboard Error loading users:', error);
    }
  };

  // Calculate process stages from remittances (변경된 프로세스: WAITING_DEPOSIT, PENDING 제거)
  // 🔥 CRITICAL FIX: useCallback으로 메모이제이션하여 TDZ 오류 방지 및 useEffect보다 위에 정의
  const calculateProcessStages = useCallback((remittances) => {
    const stages = {
      stage1: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // PROCESSING: 헥토 해외송금 처리 중
      stage2: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // UAB_PROCESSING: 수취은행 송금 처리 중
      stage3: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // COMPLETED: 송금 완료
      stage4: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }  // FAILED: 실패
    };

    remittances.forEach(remit => {
      const status = (remit.status || '').toUpperCase();
      // 🔥 USD, MMK 금액 일관성 보장 (저장된 정확한 값 사용)
      const amountUSD = Number(remit.amountUSD || remit.receiver?.amountUSD || remit.actualAmountUSD || 0);
      const amountKRW = Number(remit.amountKRW || 0);
      const amountMMK = Number(remit.amountMMK || remit.receiver?.amountMMK || 0);

      // 🔥 변경된 프로세스에 맞춘 상태 매핑 (WAITING_DEPOSIT, PENDING 제거)
      // ⚠️ status가 없거나 알 수 없는 상태인 경우 stage에 포함하지 않음
      let stageKey = null;
      if (status === 'PROCESSING') {
        stageKey = 'stage1'; // 헥토 해외송금 처리 중
      } else if (status === 'UAB_PROCESSING') {
        stageKey = 'stage2'; // 수취은행 송금 처리 중
      } else if (status === 'COMPLETED' || status === 'SUCCESS') {
        stageKey = 'stage3'; // 송금 완료
      } else if (status === 'FAILED' || status === 'ERROR' || status === 'CANCELLED') {
        stageKey = 'stage4'; // 실패
      }
      
      // stageKey가 null이면 해당 송금은 stage에 포함하지 않음
      if (!stageKey) {
        return; // 다음 송금으로 건너뛰기
      }

      stages[stageKey].count += 1;
      stages[stageKey].amountUSD += amountUSD;
      stages[stageKey].amountKRW += amountKRW;
      stages[stageKey].amountMMK += amountMMK;
    });

    setProcessStages(stages);
    console.log('📊 Process stages calculated:', stages);
    
    // 디버깅: 실제 송금 데이터의 상태 분포 확인
    const statusCounts = {};
    remittances.forEach(remit => {
      const status = (remit.status || '').toUpperCase();
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    console.log('📊 실제 송금 데이터 상태 분포:', statusCounts);
    console.log('📊 전체 송금 데이터 개수:', remittances.length);
  }, []);

  // DBA 권고사항: 저장 기준 경로 사용 - 사용자별 송금 데이터 조회 (성능 최적화 및 에러 처리 개선)
  const loadRemittancesFromUsers = useCallback(async () => {
    try {
      const startTime = performance.now();
      // 🔥 CRITICAL FIX: appId를 로컬 변수로 즉시 캡처하여 TDZ 오류 방지
      const currentAppKey = appId ? String(appId) : '';
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 AdminDashboard 송금 데이터 로딩 시작...');
      }
      
      // 모든 가능한 경로에서 송금 데이터 가져오기 (다른 모니터링 페이지와 동일)
      // 🔥 CRITICAL FIX: currentAppKey를 로컬 변수로 먼저 캡처하여 클로저 문제 방지
      const localAppKeyForPaths = currentAppKey;
      // 예약송금(scheduledRemittances)은 KPI/프로세스 단계와 섞이면 수치가 왜곡되므로 제외
      const possiblePaths = [
        `artifacts/${localAppKeyForPaths}/remittances`,  // 중앙 송금 컬렉션 (주요)
        'remittances',  // 루트 송금 컬렉션 (Backend realDataController)
        `artifacts/${localAppKeyForPaths}/users`,  // 사용자별 송금 컬렉션 (하위 컬렉션들)
      ];
      
      let allRemittances = [];
      // 🔥 CRITICAL FIX: errors 배열을 함수 스코프에서 안전하게 관리
      const errors = [];
      
      // 🔥 CRITICAL FIX: db를 로컬 변수로 캡처하여 클로저 문제 방지
      const firestoreDb = db;
      
      // 병렬 처리로 성능 개선
      // 🔥 CRITICAL FIX: currentAppKey를 로컬 변수로 캡처하여 중첩 클로저 문제 방지
      const localAppKey = currentAppKey;
      const pathPromises = possiblePaths.map(async (collectionPath) => {
        // 🔥 CRITICAL FIX: collectionPath를 로컬 변수로 캡처하여 TDZ 오류 방지
        const currentPath = collectionPath;
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 AdminDashboard 경로 시도: ${currentPath}`);
          }
          
          if (currentPath === `artifacts/${localAppKey}/users`) {
            // 사용자별 송금 컬렉션 처리 (하위 컬렉션들)
            // 🔥 CRITICAL FIX: 모든 외부 변수를 로컬 변수로 명시적으로 캡처하여 TDZ 오류 방지
            const localDb = firestoreDb;
            const localPath = currentPath;
            const localKey = localAppKey;
            
            const usersRef = collection(localDb, localPath);
            const usersSnapshot = await getDocs(usersRef);
            
            // 🔥 CRITICAL FIX: 모든 변수를 함수 최상단에서 선언하여 TDZ 완전 방지
            const userPromises = usersSnapshot.docs.map(async (userDoc) => {
              // 🔥 모든 변수를 즉시 상수로 선언 (TDZ 완전 방지)
              const userIdValue = String(userDoc.id || '');
              const dbRefValue = localDb;
              const appKeyRefValue = String(localKey || '');
              const pathRefValue = String(localPath || '');
              
              // 🔥 중첩 함수에서 사용할 모든 값을 미리 계산하고 상수로 저장
              const remittancePathString = `artifacts/${appKeyRefValue}/users/${userIdValue}/remittances`;
              const sourcePathPrefixString = `${pathRefValue}/${userIdValue}/remittances`;
              
              try {
                const userRemittancesRef = collection(dbRefValue, remittancePathString);
                const userRemittancesSnapshot = await getDocs(userRemittancesRef);
                
                // 🔥 CRITICAL FIX: for 루프 사용하여 TDZ 완전 방지
                const remittanceDocsArray = [];
                const snapshotDocs = userRemittancesSnapshot.docs;
                
                for (let i = 0; i < snapshotDocs.length; i++) {
                  const currentDoc = snapshotDocs[i];
                  const currentDocData = currentDoc.data();
                  const currentDocId = String(currentDoc.id || '');
                  
                  // 날짜 변환을 안전하게 처리
                  let createdAtValue = new Date();
                  if (currentDocData.createdAt) {
                    if (typeof currentDocData.createdAt.toDate === 'function') {
                      createdAtValue = currentDocData.createdAt.toDate();
                    } else if (currentDocData.createdAt instanceof Date) {
                      createdAtValue = currentDocData.createdAt;
                    } else {
                      createdAtValue = new Date(currentDocData.createdAt);
                    }
                  }
                  
                  let updatedAtValue = new Date();
                  if (currentDocData.updatedAt) {
                    if (typeof currentDocData.updatedAt.toDate === 'function') {
                      updatedAtValue = currentDocData.updatedAt.toDate();
                    } else if (currentDocData.updatedAt instanceof Date) {
                      updatedAtValue = currentDocData.updatedAt;
                    } else {
                      updatedAtValue = new Date(currentDocData.updatedAt);
                    }
                  }
                  
                  let completedAtValue = null;
                  if (currentDocData.completedAt) {
                    if (typeof currentDocData.completedAt.toDate === 'function') {
                      completedAtValue = currentDocData.completedAt.toDate();
                    } else if (currentDocData.completedAt instanceof Date) {
                      completedAtValue = currentDocData.completedAt;
                    } else {
                      completedAtValue = new Date(currentDocData.completedAt);
                    }
                  }
                  
                  // 객체 생성 시 모든 변수를 명시적으로 사용
                  const remittanceDoc = {
                    id: currentDocId,
                    ...currentDocData,
                    createdAt: createdAtValue,
                    updatedAt: updatedAtValue,
                    completedAt: completedAtValue,
                    _source: sourcePathPrefixString
                  };
                  
                  remittanceDocsArray.push(remittanceDoc);
                }
                
                return remittanceDocsArray;
              } catch (userError) {
                const errorMessage = userError?.message || String(userError || 'Unknown error');
                if (process.env.NODE_ENV === 'development') {
                  console.warn(`⚠️ AdminDashboard 사용자 ${userIdValue} 송금 데이터 접근 실패:`, errorMessage);
                }
                return [];
              }
            });
            
            const userResults = await Promise.all(userPromises);
            return userResults.flat();
          } else {
            // 일반 컬렉션 처리
            const remittancesRef = collection(firestoreDb, currentPath);
            const remittancesQuery = query(remittancesRef, orderBy('createdAt', 'desc'));
            
            const remittancesSnapshot = await getDocs(remittancesQuery);
            if (process.env.NODE_ENV === 'development') {
              console.log(`📊 AdminDashboard ${currentPath} - 문서 개수:`, remittancesSnapshot.size);
            }
            
            // 🔥 CRITICAL FIX: map 대신 for 루프 사용하여 TDZ 완전 방지
            const remittanceDocs = [];
            const pathValue = String(currentPath || '');
            for (const doc of remittancesSnapshot.docs) {
              const docData = doc.data();
              const docId = doc.id;
              const createdAtValue = docData.createdAt?.toDate?.() || new Date(docData.createdAt || Date.now());
              const updatedAtValue = docData.updatedAt?.toDate?.() || new Date(docData.updatedAt || Date.now());
              const completedAtValue = docData.completedAt?.toDate?.() || null;
              
              remittanceDocs.push({
                id: docId,
                ...docData,
                createdAt: createdAtValue,
                updatedAt: updatedAtValue,
                completedAt: completedAtValue,
                _source: pathValue
            });
            }
            return remittanceDocs;
          }
        } catch (pathError) {
          // 🔥 CRITICAL FIX: errors 배열 참조를 안전하게 처리 (에러만 로깅)
          const errorMessage = pathError?.message || String(pathError);
          if (process.env.NODE_ENV === 'development') {
            console.warn(`⚠️ AdminDashboard ${currentPath} 접근 실패:`, errorMessage);
          }
          return [];
        }
      });
      
      const pathResults = await Promise.all(pathPromises);
      allRemittances = pathResults.flat();
      
      if (errors.length > 0 && process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ 일부 경로에서 오류 발생 (${errors.length}개):`, errors);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 AdminDashboard 전체 로드된 송금 데이터:', allRemittances.length, '개');
      }
      
      // 🔥 개선된 중복 제거 로직: ID뿐만 아니라 userId + amount + createdAt 조합으로 중복 체크
      // 같은 송금이 다른 경로에 있으면 다른 ID를 가질 수 있으므로 더 정확한 중복 체크 필요
      const uniqueRemittancesById = new Map(); // ID 기반 (주요)
      const uniqueRemittancesByKey = new Map(); // 고유 키 기반 (보조)
      let duplicateCount = 0;
      
      // 우선순위 정렬: 메인 컬렉션 > 사용자별 컬렉션 > 기타
      const getPriority = (source) => {
        if (!source) return 3;
        if (source.includes('/remittances') && !source.includes('/users/')) return 1; // 메인 컬렉션
        if (source.includes('/users/') && source.includes('/remittances')) return 2; // 사용자별 컬렉션
        return 3; // 기타
      };
      
      // 우선순위가 높은 순서로 정렬
      allRemittances.sort((a, b) => getPriority(a._source) - getPriority(b._source));
      
      allRemittances.forEach(remit => {
        // 고유 키 생성: userId + amountUSD + createdAt (밀리초 단위)
        const userId = remit.userId || remit.sender?.userId || remit.senderId || '';
        const amountUSD = Number(remit.amountUSD || remit.receiver?.amountUSD || remit.actualAmountUSD || 0);
        const createdAt = remit.createdAt ? (remit.createdAt instanceof Date ? remit.createdAt.getTime() : new Date(remit.createdAt).getTime()) : 0;
        
        // 고유 키: userId_amountUSD_createdAt (같은 사용자가 같은 금액을 같은 시간에 보낼 가능성은 거의 없음)
        const uniqueKey = `${userId}_${amountUSD}_${createdAt}`;
        
        // ID 기반 중복 체크 (우선)
        if (uniqueRemittancesById.has(remit.id)) {
          duplicateCount++;
          if (process.env.NODE_ENV === 'development') {
            console.log(`⚠️ 중복된 송금 ID 발견: ${remit.id} (경로: ${remit._source})`);
          }
          return; // 이미 존재하는 ID는 스킵
        }
        
        // 고유 키 기반 중복 체크 (ID가 다른 경우)
        if (uniqueRemittancesByKey.has(uniqueKey)) {
          const existingRemit = uniqueRemittancesByKey.get(uniqueKey);
          // 우선순위 비교: 현재 데이터가 더 높은 우선순위면 교체
          const currentPriority = getPriority(remit._source);
          const existingPriority = getPriority(existingRemit._source);
          
          if (currentPriority < existingPriority) {
            // 현재 데이터가 더 높은 우선순위 - 기존 데이터 제거하고 새 데이터 추가
            if (uniqueRemittancesById.has(existingRemit.id)) {
              uniqueRemittancesById.delete(existingRemit.id);
            }
            uniqueRemittancesByKey.set(uniqueKey, remit);
            uniqueRemittancesById.set(remit.id, remit);
            duplicateCount++;
            if (process.env.NODE_ENV === 'development') {
              console.log(`🔄 중복된 송금 교체 (고유 키): ${uniqueKey} (기존 ID: ${existingRemit.id} → 새 ID: ${remit.id})`);
            }
          } else {
            duplicateCount++;
            if (process.env.NODE_ENV === 'development') {
              console.log(`⚠️ 중복된 송금 발견 (고유 키): ${uniqueKey} (ID: ${remit.id}, 경로: ${remit._source}) - 기존 데이터 유지`);
            }
          }
          return;
        }
        
        // 새로운 송금 데이터 추가
        uniqueRemittancesById.set(remit.id, remit);
        uniqueRemittancesByKey.set(uniqueKey, remit);
      });
      
      // ID 기반으로 최종 결과 생성
      const uniqueRemittances = Array.from(uniqueRemittancesById.values());
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 중복 제거 완료: ${allRemittances.length}개 → ${uniqueRemittances.length}개 (${duplicateCount}개 중복 제거됨)`);
      }
      
      const endTime = performance.now();
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ AdminDashboard 송금 데이터 로딩 완료 (${(endTime - startTime).toFixed(2)}ms, ${uniqueRemittances.length}개)`);
      }
      
      setRemits(uniqueRemittances);
      
      // Calculate process stages - 함수 내부에서 직접 호출하여 TDZ 오류 방지
      const stages = {
        stage1: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // PROCESSING: 헥토 해외송금 처리 중
        stage2: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // UAB_PROCESSING: 수취은행 송금 처리 중
        stage3: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // COMPLETED: 송금 완료
        stage4: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }  // FAILED: 실패
      };

      uniqueRemittances.forEach(remit => {
        const status = (remit.status || '').toUpperCase();
        // 🔥 USD, MMK 금액 일관성 보장 (저장된 정확한 값 사용)
        const amountUSD = Number(remit.amountUSD || remit.receiver?.amountUSD || remit.actualAmountUSD || 0);
        const amountKRW = Number(remit.amountKRW || 0);
        const amountMMK = Number(remit.amountMMK || remit.receiver?.amountMMK || 0);

        // 🔥 변경된 프로세스에 맞춘 상태 매핑 (WAITING_DEPOSIT, PENDING 제거)
        // ⚠️ status가 없거나 알 수 없는 상태인 경우 stage에 포함하지 않음
        let stageKey = null;
        if (status === 'PROCESSING') {
          stageKey = 'stage1'; // 헥토 해외송금 처리 중
        } else if (status === 'UAB_PROCESSING') {
          stageKey = 'stage2'; // 수취은행 송금 처리 중
        } else if (status === 'COMPLETED' || status === 'SUCCESS') {
          stageKey = 'stage3'; // 송금 완료
        } else if (status === 'FAILED' || status === 'ERROR' || status === 'CANCELLED') {
          stageKey = 'stage4'; // 실패
        }
        
        // stageKey가 null이면 해당 송금은 stage에 포함하지 않음
        if (!stageKey) {
          return; // 다음 송금으로 건너뛰기
        }

        stages[stageKey].count += 1;
        stages[stageKey].amountUSD += amountUSD;
        stages[stageKey].amountKRW += amountKRW;
        stages[stageKey].amountMMK += amountMMK;
      });

      setProcessStages(stages);
    } catch (error) {
      console.error('❌ AdminDashboard 송금 데이터 로딩 실패:', {
        error: error.message,
        stack: error.stack,
        code: error.code,
        name: error.name
      });
      
      // 에러 발생 시에도 기존 데이터 유지 (사용자 경험 개선)
      setRemits([]);
    }
  }, [appId, setRemits, setProcessStages]);

  // Live subscriptions
  useEffect(() => {
    // 설정을 먼저 로드한 후 사용자 데이터 로드
    const initializeData = async () => {
      await loadSettings();
      loadUsersWithProfiles();
      // Cloud Functions에서 Policy Compliance 통계 로드 (Policy Compliance Management System과 동일한 데이터)
      loadComplianceStatsFromCloud();
    };
    
    initializeData();

    // Load remittances initially
    loadRemittancesFromUsers();
    
    const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5분
    const refreshDashboardData = () => {
      if (document.hidden) return;
      loadUsersWithProfiles();
      loadRemittancesFromUsers();
      loadComplianceStatsFromCloud(); // Cloud Functions 사용
    };

    let userRefreshInterval = null;
    const startInterval = () => {
      if (userRefreshInterval) return;
      userRefreshInterval = setInterval(refreshDashboardData, REFRESH_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (!userRefreshInterval) return;
      clearInterval(userRefreshInterval);
      userRefreshInterval = null;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
        return;
      }
      refreshDashboardData();
      startInterval();
    };

    if (!document.hidden) {
      startInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => { 
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  // settings가 변경될 때마다 Policy Compliance 다시 계산
  useEffect(() => {
    if (settings.requiredMonthlyAmount !== 300) { // 기본값이 아닌 경우에만
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 AdminDashboard 설정값 변경됨, Policy Compliance 다시 계산:', settings);
      }
      loadComplianceStatsFromCloud(); // Cloud Functions 사용
    }
  }, [settings]);

  // Direct consultation loading (same as AdminConsultationDashboard)
  // 🔥 CRITICAL FIX: users state를 기본값으로 사용하지 않음 (TDZ 오류 방지)
  const loadConsultationsDirectly = async (userDocs = null) => {
    try {
      const APP_KEY = appId || appId;
      const consultationData = [];
      
      // If no userDocs provided, fetch them
      // 🔥 CRITICAL FIX: users state를 직접 참조하지 않음 (TDZ 오류 방지)
      if (!userDocs || userDocs.length === 0) {
        const usersSnapshot = await getDocs(collection(db, "artifacts", APP_KEY, "users"));
        userDocs = usersSnapshot.docs;
      }

      for (const userDoc of userDocs) {
        const uid = userDoc.id || userDoc;
        const consRef = collection(db, "artifacts", APP_KEY, "users", uid, "consultations");
        const q = query(consRef, orderBy("createdAt", "desc"));
        const consSnapshot = await getDocs(q);

        consSnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          consultationData.push({
            id: docSnap.id,
            uid: uid,
            userName: data.userName || "?�명",
            type: data.type || 'etc',
            title: data.title,
            content: data.content,
            createdAt: data.createdAt?.toDate
              ? data.createdAt.toDate()
              : data.createdAt
              ? new Date(data.createdAt)
              : undefined,
            answers: data.answers || [],
            answered: !!data.answered || (Array.isArray(data.answers) && data.answers.length > 0),
            resolvedAt: data.resolvedAt?.toDate
              ? data.resolvedAt.toDate()
              : data.resolvedAt
              ? new Date(data.resolvedAt)
              : undefined,
          });
        });
      }

      setConsults(consultationData);
      
      // Calculate KPIs directly from loaded data
      calculateConsultationKPIs(consultationData);
      
    } catch (err) {
      console.error('Error loading consultations:', err);
    }
  };

  // Calculate consultation KPIs
  const calculateConsultationKPIs = (consultationData) => {
    let totalCount = consultationData.length;
    let totalAnswered = 0;

    consultationData.forEach(consultation => {
      const isAnswered = consultation.answered || (Array.isArray(consultation.answers) && consultation.answers.length > 0);
      
      if (isAnswered) {
        totalAnswered++;
      }
    });

    // Set consultation counts
    setConsultCounts({ 
      total: totalCount, 
      answered: totalAnswered 
    });
  };

  // 송금 프로세스만 선택적으로 업데이트하는 상태
  const [isProcessUpdating, setIsProcessUpdating] = useState(false);
  const [processUpdateInterval, setProcessUpdateInterval] = useState(null);

  // 송금 프로세스만 업데이트하는 함수 (성능 최적화 및 에러 처리 개선)
  const updateProcessStagesOnly = useCallback(async () => {
    if (document.hidden) {
      return;
    }
    // 중복 요청 방지
    if (isProcessUpdating) {
      console.log('⚠️ 이미 업데이트 중입니다.');
      return;
    }

    try {
      setIsProcessUpdating(true);
      const startTime = performance.now();
      console.log('🔄 송금 프로세스만 업데이트 중...');
      
      // 타임아웃 설정 (30초)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('업데이트 타임아웃')), 30000);
      });

      // 송금 데이터만 로드 (전체 대시보드 새로고침 없이)
      const APP_KEY = appId || appId;
      const remittancesRef = collection(db, `artifacts/${APP_KEY}/remittances`);
      const q = query(remittancesRef, orderBy('createdAt', 'desc'));
      
      // Promise.race로 타임아웃 처리
      const snapshot = await Promise.race([
        getDocs(q),
        timeoutPromise
      ]);
      
      const remittances = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // 프로세스 단계별로 분류 (변경된 프로세스: WAITING_DEPOSIT, PENDING 제거)
      const stages = {
        stage1: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // PROCESSING: 헥토 해외송금 처리 중
        stage2: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // UAB_PROCESSING: 수취은행 송금 처리 중
        stage3: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }, // COMPLETED: 송금 완료
        stage4: { count: 0, amountKRW: 0, amountUSD: 0, amountMMK: 0 }  // FAILED: 실패
      };
      
      remittances.forEach(remit => {
        const status = remit.status;
        // 🔥 USD, MMK 금액 일관성 보장 (저장된 정확한 값 사용) - actualAmountUSD도 확인
        const amountKRW = Number(remit.amountKRW || 0);
        const amountUSD = Number(remit.amountUSD || remit.receiver?.amountUSD || remit.actualAmountUSD || 0);
        const amountMMK = Number(remit.amountMMK || remit.receiver?.amountMMK || 0);
        
        const statusUpper = (status || '').toUpperCase();
        // 🔥 변경된 프로세스에 맞춘 상태 매핑 (WAITING_DEPOSIT, PENDING 제거)
        if (statusUpper === 'PROCESSING') {
          stages.stage1.count++;
          stages.stage1.amountKRW += amountKRW;
          stages.stage1.amountUSD += amountUSD;
          stages.stage1.amountMMK += amountMMK;
        } else if (statusUpper === 'UAB_PROCESSING') {
          stages.stage2.count++;
          stages.stage2.amountKRW += amountKRW;
          stages.stage2.amountUSD += amountUSD;
          stages.stage2.amountMMK += amountMMK;
        } else if (statusUpper === 'COMPLETED' || statusUpper === 'SUCCESS') {
          stages.stage3.count++;
          stages.stage3.amountKRW += amountKRW;
          stages.stage3.amountUSD += amountUSD;
          stages.stage3.amountMMK += amountMMK;
        } else if (statusUpper === 'FAILED' || statusUpper === 'ERROR' || statusUpper === 'CANCELLED') {
          stages.stage4.count++;
          stages.stage4.amountKRW += amountKRW;
          stages.stage4.amountUSD += amountUSD;
          stages.stage4.amountMMK += amountMMK;
        }
      });
      
      setProcessStages(stages);
      setRemits(prevRemits => {
        // 성능 최적화: 변경된 데이터만 업데이트
        const remitMap = new Map(prevRemits.map(r => [r.id, r]));
        remittances.forEach(remit => {
          remitMap.set(remit.id, remit);
        });
        return Array.from(remitMap.values());
      });
      
      const endTime = performance.now();
      console.log(`✅ 송금 프로세스 업데이트 완료 (${(endTime - startTime).toFixed(2)}ms)`);
    } catch (error) {
      console.error('❌ 송금 프로세스 업데이트 실패:', {
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // 에러 타입별 처리
      if (error.message === '업데이트 타임아웃') {
        console.warn('⚠️ 업데이트 타임아웃 - 다음 주기에서 재시도');
      } else if (error.code === 'unavailable') {
        console.warn('⚠️ Firebase 서비스 일시적으로 사용 불가 - 재시도 예정');
      } else {
        console.error('❌ 예상치 못한 오류:', error);
      }
    } finally {
      setIsProcessUpdating(false);
    }
  }, [isProcessUpdating, appId]);

  // WebSocket 연결 및 실시간 업데이트 (개선된 에러 처리 및 재연결 로직)
  useEffect(() => {
    let reconnectTimeout = null;
    let updateDebounceTimeout = null;
    const DEBOUNCE_DELAY = 1000; // 1초 디바운스
    
    // 디바운스된 업데이트 함수
    const debouncedUpdate = () => {
      if (updateDebounceTimeout) {
        clearTimeout(updateDebounceTimeout);
      }
      updateDebounceTimeout = setTimeout(() => {
        updateProcessStagesOnly();
      }, DEBOUNCE_DELAY);
    };
    
    // WebSocket 연결
    try {
      webSocketService.connect();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ WebSocket 연결 시도 실패:', error);
      }
      setWsConnected(false);
    }
    
    // WebSocket 이벤트 리스너 등록
    const handleConnected = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 AdminDashboard WebSocket 연결됨');
      }
      setWsConnected(true);
      // 연결 성공 시 즉시 업데이트
      updateProcessStagesOnly();
    };
    
    const handleDisconnected = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 AdminDashboard WebSocket 연결 해제됨');
      }
      setWsConnected(false);
      
      // 자동 재연결 시도 (exponential backoff)
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      
      const reconnectDelay = Math.min(1000 * Math.pow(2, 0), 30000); // 최대 30초
      reconnectTimeout = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 WebSocket 재연결 시도...');
        }
        try {
          webSocketService.connect();
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ WebSocket 재연결 실패:', error);
          }
        }
      }, reconnectDelay);
    };
    
    const handleError = (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('🔌 AdminDashboard WebSocket 에러:', error);
      }
      setWsConnected(false);
    };
    
    const handleRemittanceUpdate = (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 AdminDashboard 송금 상태 업데이트 수신:', data);
      }
      // 디바운스된 업데이트 (빈번한 업데이트 방지)
      debouncedUpdate();
    };
    
    const handleDashboardStats = (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 AdminDashboard 통계 업데이트 수신:', data);
      }
      // 디바운스된 업데이트 (빈번한 업데이트 방지)
      debouncedUpdate();
    };
    
    // 이벤트 리스너 등록
    webSocketService.on('connected', handleConnected);
    webSocketService.on('disconnected', handleDisconnected);
    webSocketService.on('error', handleError);
    webSocketService.on('remittance_update', handleRemittanceUpdate);
    webSocketService.on('dashboard_stats', handleDashboardStats);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      webSocketService.off('connected', handleConnected);
      webSocketService.off('disconnected', handleDisconnected);
      webSocketService.off('error', handleError);
      webSocketService.off('remittance_update', handleRemittanceUpdate);
      webSocketService.off('dashboard_stats', handleDashboardStats);
      
      // 타임아웃 정리
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (updateDebounceTimeout) {
        clearTimeout(updateDebounceTimeout);
      }
      
      // WebSocket 연결 해제는 서비스 레벨에서 관리
      // webSocketService.disconnect(); // 다른 컴포넌트에서 사용 중일 수 있으므로 주석 처리
      
      // 인터벌 정리
      if (processUpdateInterval) {
        clearInterval(processUpdateInterval);
      }
    };
  }, [updateProcessStagesOnly]);

  // 송금 프로세스가 활성화되어 있을 때만 5분마다 업데이트
  useEffect(() => {
    const totalProcessCount = Object.values(processStages).reduce((sum, stage) => sum + stage.count, 0);
    
    if (totalProcessCount > 0) {
      console.log('🚀 송금 프로세스 활성화됨, 5분마다 업데이트 시작');
      
      // 기존 인터벌 정리
      if (processUpdateInterval) {
        clearInterval(processUpdateInterval);
      }
      
      // 5분마다 송금 프로세스만 업데이트 (300000ms = 5분)
      const interval = setInterval(() => {
        updateProcessStagesOnly();
      }, 300000);
      
      setProcessUpdateInterval(interval);
    } else {
      console.log('⏸️ 송금 프로세스 비활성화됨, 업데이트 중지');
      
      // 인터벌 정리
      if (processUpdateInterval) {
        clearInterval(processUpdateInterval);
        setProcessUpdateInterval(null);
      }
    }
    
    return () => {
      if (processUpdateInterval) {
        clearInterval(processUpdateInterval);
      }
    };
  }, [processStages]);

  // Load consultations on component mount
  useEffect(() => {
    // 🔥 CRITICAL FIX: users state를 직접 전달하지 않고, 함수 내부에서 fetch하도록 수정 (TDZ 오류 방지)
    loadConsultationsDirectly();
  }, []);

  // Helpers — Firestore Timestamp / Date / number / ISO 문자열 모두 지원
  const toDateKey = (ts) => {
    let date;
    if (ts == null || ts === '') {
      date = new Date();
    } else if (typeof ts?.toDate === 'function') {
      date = ts.toDate();
    } else if (ts instanceof Date) {
      date = ts;
    } else if (typeof ts === 'number' && !Number.isNaN(ts)) {
      date = new Date(ts);
    } else if (typeof ts === 'string') {
      date = new Date(ts);
    } else {
      date = new Date();
    }
    if (Number.isNaN(date.getTime())) {
      date = new Date();
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const lastNDays = (n) => {
    const days = [];
    const now = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      days.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    }
    return days;
  };

  // Build trends (last 30 days)
  const days = useMemo(() => lastNDays(30), []);

  const remittanceTrend = useMemo(() => {
    console.log('📊 송금 차트 계산 시작, remits 개수:', remits.length);
    const map = Object.fromEntries(days.map(k => [k, { date: k, amount: 0, count: 0 }]));
    remits.forEach((t, index) => {
      // 🔥 USD 금액 일관성 보장 (저장된 정확한 값 사용)
      const amountUSD = Number(t.amountUSD || t.receiver?.amountUSD || t.actualAmountUSD || 0);
      console.log(`📊 송금 ${index + 1}:`, {
        status: t.status,
        amountUSD: amountUSD,
        createdAt: t.createdAt,
        dateKey: toDateKey(t.createdAt || t.created_at || t.date)
      });
      if ((t.status || '').toUpperCase() !== 'COMPLETED') return;
      const key = toDateKey(t.createdAt || t.created_at || t.date);
      if (map[key]) { 
        map[key].amount += amountUSD; 
        map[key].count += 1; 
        console.log(`📊 차트 데이터 업데이트: ${key}에 ${amountUSD} 추가`);
      }
    });
    const result = Object.values(map);
    console.log('📊 AdminDashboard 송금 차트 데이터:', result);
    return result;
  }, [remits, days]);

  const signupTrend = useMemo(() => {
    const map = Object.fromEntries(days.map(k => [k, { date: k, users: 0 }]));
    users.forEach(u => {
      const key = toDateKey(u.createdAt || u.created_at || u.joinedAt);
      if (map[key]) { map[key].users += 1; }
    });
    return Object.values(map);
  }, [users, days]);

  // Consultation by type (answered vs pending)
  // Consultations by type rows are aggregated via one-shot getDocs above

  // KPIs
  const kpis = useMemo(() => {
    const totalUsers = totalUsersCount;
    // Remittance KPIs from transactions
    const todayKey = toDateKey(new Date());
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const rangeStart = (() => {
      if (range === '7') { const d = new Date(); d.setDate(d.getDate()-7); return d; }
      if (range === '30') { const d = new Date(); d.setDate(d.getDate()-30); return d; }
      if (range === '90') { const d = new Date(); d.setDate(d.getDate()-90); return d; }
      return startOfYear;
    })();
    const isInRange = (ts) => {
      let d;
      if (ts == null || ts === '') return false;
      if (typeof ts?.toDate === 'function') d = ts.toDate();
      else if (ts instanceof Date) d = ts;
      else if (typeof ts === 'number' && !Number.isNaN(ts)) d = new Date(ts);
      else if (typeof ts === 'string') d = new Date(ts);
      else d = new Date();
      if (Number.isNaN(d.getTime())) return false;
      return d >= rangeStart && d <= now;
    };
    let totalRemit = 0; // completed sum (전체 기간)
    let todayRemit = 0; // completed sum today
    let pendingAmount = 0; // pending sum
    
    // remits 데이터를 사용하여 송금 통계 계산 (COMPLETED + SUCCESS 상태, 전체 기간)
    remits.forEach(t => {
      // 🔥 USD 금액 일관성 보장 (저장된 정확한 값 사용)
      const amt = Number(t.amountUSD || t.receiver?.amountUSD || t.actualAmountUSD || 0);
      const status = (t.status || '').toUpperCase();
      const created = t.createdAt || t.created_at || t.date;
      const key = toDateKey(created);
      
      // COMPLETED 또는 SUCCESS 상태 계산 (전체 기간, Remittance Process와 일치)
      if ((status === 'COMPLETED' || status === 'SUCCESS')) {
        totalRemit += amt; // 전체 기간 합계
        if (isInRange(created) && key === todayKey) {
          todayRemit += amt; // 오늘 날짜만 필터링
        }
      } else if ((status === 'PROCESSING' || status === 'UAB_PROCESSING') && isInRange(created)) {
        pendingAmount += amt;
      }
    });
    
    console.log('📊 AdminDashboard KPI 계산:', {
      remitsCount: remits.length,
      totalRemit,
      todayRemit,
      pendingAmount,
      range
    });
    const openConsults = consults.filter(c => (c.status || '').toUpperCase() !== 'RESOLVED').length;
    return { totalUsers, totalRemit, todayRemit, pendingAmount, openConsults };
  }, [range, totalUsersCount, users, remits, consults]);

  // Day-over-day deltas
  const deltas = useMemo(() => {
    const yesterdayKey = (() => { const d = new Date(); d.setDate(d.getDate()-1); return toDateKey(d); })();
    let yTodayRemit = 0;
    let yPending = 0;
    let yUsers = 0;
    // Yesterday users by profiles snapshot approximation: use current total (no historical); show N/A by 0 delta if unavailable
    // If you want exact DoD, store daily snapshots.
    remits.forEach(t => {
      // 🔥 USD 금액 일관성 보장 (저장된 정확한 값 사용)
      const amt = Number(t.amountUSD || t.receiver?.amountUSD || t.actualAmountUSD || 0);
      const status = (t.status || '').toUpperCase();
      const key = toDateKey(t.createdAt || t.created_at || t.date);
      if (status === 'COMPLETED' && key === yesterdayKey) yTodayRemit += amt;
      if ((status === 'PROCESSING' || status === 'UAB_PROCESSING') && key === yesterdayKey) yPending += amt;
    });
    return {
      todayRemitDelta: kpis.todayRemit - yTodayRemit,
      pendingDelta: kpis.pendingAmount - yPending,
      usersDelta: 0, // placeholder, see note above
    };
  }, [remits, kpis.todayRemit, kpis.pendingAmount]);

  // Consultation metrics
  const consultMetrics = useMemo(() => {
    const getDate = (v) => (v?.toDate?.() ? v.toDate() : (v instanceof Date ? v : (typeof v === 'number' ? new Date(v) : null)));
    let sumHours = 0;
    let resolvedCount = 0;
    let overduePending = 0;
    const now = new Date();

    consults.forEach(c => {
      const created = getDate(c.createdAt || c.openedAt);
      const resolved = getDate(c.resolvedAt);
      const status = (c.status || (Array.isArray(c.answers) && c.answers.length > 0 ? 'RESOLVED' : 'PENDING')).toUpperCase();
      if (created) {
        if (resolved) {
          const hours = Math.max(0, (resolved.getTime() - created.getTime()) / 36e5);
          sumHours += hours;
          resolvedCount += 1;
        } else if (status !== 'RESOLVED') {
          const ageHours = Math.max(0, (now.getTime() - created.getTime()) / 36e5);
          if (ageHours > 48) overduePending += 1;
        }
      }
    });

    const avgResolutionHours = resolvedCount > 0 ? sumHours / resolvedCount : 0;
    return { avgResolutionHours, overduePending };
  }, [consults]);


  const statusMix = useMemo(() => {
    const totals = { processingUSD: 0, completedUSD: 0, failedUSD: 0 };
    remits.forEach((t) => {
      const status = (t.status || '').toUpperCase();
      const amt = Number(t.amountUSD || t.receiver?.amountUSD || t.actualAmountUSD || 0);
      if (status === 'PROCESSING' || status === 'UAB_PROCESSING') totals.processingUSD += amt;
      else if (status === 'COMPLETED') totals.completedUSD += amt;
      else if (status === 'FAILED') totals.failedUSD += amt;
    });
    return totals;
  }, [remits]);

  const recentOpsFeed = useMemo(() => {
    const getTs = (item) => {
      const raw = item.updatedAt || item.createdAt || item.completedAt || item.timestamp;
      if (!raw) return 0;
      if (raw?.toDate) return raw.toDate().getTime();
      if (raw instanceof Date) return raw.getTime();
      const n = new Date(raw).getTime();
      return Number.isFinite(n) ? n : 0;
    };

    return [...remits]
      .sort((a, b) => getTs(b) - getTs(a))
      .slice(0, 8)
      .map((item) => {
        const status = String(item.status || '').toUpperCase();
        let state = 'processing';
        if (status === 'COMPLETED' || status === 'SUCCESS') state = 'cleared';
        else if (status === 'FAILED' || status === 'REJECTED') state = 'failed';
        const ts = getTs(item);
        return {
          key: item.id,
          at: ts || null,
          corridor: item.corridor || item.route,
          amountUSD: Number(item.amountUSD || item.receiver?.amountUSD || item.actualAmountUSD || 0),
          state,
          status: state === 'processing' ? status || 'PROCESSING' : null,
        };
      });
  }, [remits]);

  const hudModel = {
    kpis: {
      totalRemit: kpis.totalRemit,
      todayRemit: kpis.todayRemit,
      todayRemitDelta: deltas.todayRemitDelta,
      totalUsers: kpis.totalUsers,
    },
    compliance: complianceStats,
    stages: processStages,
    totalRemittances: remits.length,
    online: wsConnected,
    syncing: isProcessUpdating,
    lastUpdate: new Date(),
    remittanceTrend,
    signupTrend,
    statusMix,
    feed: recentOpsFeed,
  };

  return (
    <CommandHud
      model={hudModel}
      range={range}
      onRangeChange={setRange}
      logoSrc="/images/mol-logo.png"
    />
  );
};

export default AdminDashboard;
